import React, { useState } from 'react';
import {
  Video,
  Headset,
  DollarSign,
  Radio,
  Shield,
} from 'lucide-react';
import racketImg from '../../assets/images/racket.webp';

const accordionItems = [
  {
    id: 1,
    title: 'Vídeos Full HD+',
    description: 'Gravação contínua em alta definição para você não perder nenhum lance.',
    icon: Video,
    imageUrl: racketImg,
    fallbackGradient: 'from-orange-500 to-amber-600',
  },
  {
    id: 2,
    title: 'Suporte 24/7',
    description: 'Nossa equipe está disponível todos os dias, a qualquer hora.',
    icon: Headset,
    imageUrl: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?q=80&w=2070&auto=format&fit=crop',
    fallbackGradient: 'from-blue-500 to-cyan-600',
  },
  {
    id: 3,
    title: 'Sem Custo de Implantação',
    description: 'Instalamos a câmera sem nenhum custo adicional para você.',
    icon: DollarSign,
    imageUrl: 'https://images.unsplash.com/photo-1574629810360-7efbbe195018?q=80&w=2073&auto=format&fit=crop',
    fallbackGradient: 'from-emerald-500 to-green-600',
  },
  {
    id: 4,
    title: 'Transmissões ao Vivo',
    description: 'Acompanhe os jogos em tempo real de onde você estiver.',
    icon: Radio,
    imageUrl: 'https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?q=80&w=2070&auto=format&fit=crop',
    fallbackGradient: 'from-red-500 to-rose-600',
  },
  {
    id: 5,
    title: 'À Prova D\'Água',
    description: 'Câmera resistente a chuva e sol, feita para arenas ao ar livre.',
    icon: Shield,
    imageUrl: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?q=80&w=2090&auto=format&fit=crop',
    fallbackGradient: 'from-violet-500 to-purple-600',
  },
];

interface AccordionItemProps {
  item: typeof accordionItems[0];
  isActive: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

const AccordionItemCard = ({ item, isActive, onMouseEnter, onClick }: AccordionItemProps) => {
  const IconComponent = item.icon;

  return (
    <div
      className={`
        relative h-[420px] md:h-[450px] rounded-2xl overflow-hidden cursor-pointer
        transition-all duration-700 ease-in-out shadow-lg
        ${isActive ? 'w-[320px] md:w-[400px]' : 'w-[55px] md:w-[60px]'}
      `}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
    >
      {/* Background with gradient fallback */}
      <div className={`absolute inset-0 bg-gradient-to-br ${item.fallbackGradient}`} />
      <img
        src={item.imageUrl}
        alt={item.title}
        className="absolute inset-0 w-full h-full object-cover"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Active content */}
      {isActive && (
        <div className="absolute bottom-0 left-0 right-0 p-6 transition-opacity duration-500">
          <div className="flex items-center gap-2 mb-2">
            <IconComponent className="w-5 h-5 text-orange-400" />
            <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
              {item.title}
            </span>
          </div>
          <p className="text-white/80 text-sm leading-relaxed">
            {item.description}
          </p>
        </div>
      )}

      {/* Inactive vertical label */}
      {!isActive && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-white text-base font-semibold whitespace-nowrap rotate-90 drop-shadow-lg">
            {item.title}
          </span>
        </div>
      )}
    </div>
  );
};

export function LandingAccordionItem() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="py-16 md:py-24 px-4" style={{ background: 'linear-gradient(135deg, #1E1E1E 0%, #232323 100%)' }}>
      <div className="container mx-auto max-w-7xl">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

          {/* Left Side: Text Content */}
          <div className="w-full lg:w-5/12 text-center lg:text-left">
            <span className="inline-block text-orange-400 text-sm font-bold uppercase tracking-widest mb-4">
              Por que escolher a Apertai?
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight tracking-tight">
              Tudo que sua arena precisa em um só lugar
            </h2>
            <p className="mt-6 text-base md:text-lg text-gray-400 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              Câmeras inteligentes, gravação automática, transmissões ao vivo e suporte dedicado.
              Sem surpresas, sem taxas escondidas.
            </p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center lg:justify-start">
              <a
                href="/contract-camera"
                className="inline-block text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 no-underline"
                style={{ background: 'linear-gradient(135deg, #e57605, #f29b2a)' }}
              >
                Contratar Agora
              </a>
              <a
                href="/login"
                className="inline-block bg-transparent border-2 border-gray-600 text-gray-300 font-semibold px-8 py-3 rounded-lg hover:border-orange-400 hover:text-orange-400 transition-colors duration-300 no-underline"
              >
                Acessar Plataforma
              </a>
            </div>
          </div>

          {/* Right Side: Image Accordion */}
          <div className="w-full lg:w-7/12">
            <div className="flex flex-row items-center justify-center gap-2 md:gap-4 overflow-x-auto p-2 md:p-4">
              {accordionItems.map((item, index) => (
                <AccordionItemCard
                  key={item.id}
                  item={item}
                  isActive={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => setActiveIndex(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
